import React, { useEffect, useState } from "react";
import Link from "next/link";
import classNames from "classnames";
import useIsMobile from "@src/hooks/useIsMobile";
import { CURRENT_YEAR } from "@shared/config/Date";
import { SLOGAN_ADDITIONAL_TEXT, SLOGAN_TEXT } from "@shared/config/GlobalText";
import { ExpandIcon } from "@shared/ui/Icon/ui/Common/ExpandIcon";
import { Logo } from "@shared/ui/logo";
import {
    CONTACTS,
    COPYRIGHT_NOTICE,
    DOCUMENTS,
    ExpandState,
    MEDIA,
    NAVIGATION,
    SOCIALS
} from "./constants";
import { DownloadApp } from "./download-app";
import { Payments } from "./payments";
import classes from "./footer.module.scss";

const Footer = () => {
    const isMobile = useIsMobile();

    const [expandedState, setExpandedState] = useState<ExpandState>({
        navigation: true,
        contacts: true,
        media: true
    });

    const toggleExpand = (key: keyof ExpandState) => {
        setExpandedState((prevState) => ({
            ...prevState,
            [key]: !prevState[key]
        }));
    };

    const handleExpandButtonClick = (key: keyof ExpandState) => {
        if (!isMobile) {
            return;
        }
        return (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            toggleExpand(key);
        };
    };

    useEffect(() => {
        if (!isMobile) {
            setExpandedState({
                navigation: true,
                contacts: true,
                media: true
            });
        }
    }, [isMobile]);

    return (
        <footer className={classes.footer}>
            <div className={classes.footer__wrapper}>
                <div className={classes.footer__inner}>
                    <div className={classes.footer__logo}>
                        <Logo className={classes["footer__logo--icon"]} />
                        <div className={classes.footer__slogan}>
                            {SLOGAN_TEXT} {SLOGAN_ADDITIONAL_TEXT}
                        </div>
                    </div>

                    <div className={classes.footer__column}>
                        <label
                            onClick={handleExpandButtonClick("navigation")}
                            className={classes.footer__label}
                        >
                            {NAVIGATION.LABEL}
                            <ExpandIcon
                                className={classNames(
                                    classes.footer__expand,
                                    expandedState.navigation && classes["footer__expand--rotated"]
                                )}
                            />
                        </label>
                        {expandedState.navigation && (
                            <ul className={classes.footer__list}>
                                {NAVIGATION.ITEMS.map((item, index) => (
                                    <li key={index}>
                                        <Link href={item.LINK} aria-label={item.ARIA_LABEL}>
                                            {item.TEXT}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className={classes.footer__column}>
                        <label
                            onClick={handleExpandButtonClick("contacts")}
                            className={classes.footer__label}
                        >
                            {CONTACTS.LABEL}
                            <ExpandIcon
                                className={classNames(
                                    classes.footer__expand,
                                    expandedState.contacts && classes["footer__expand--rotated"]
                                )}
                            />
                        </label>
                        {expandedState.contacts && (
                            <ul className={classes.footer__list}>
                                <li>
                                    <Link
                                        href={"/contacts"}
                                        rel="noopener noreferrer"
                                        aria-label={CONTACTS.ITEMS[0].ARIA_LABEL}
                                    >
                                        Общие контакты
                                    </Link>
                                </li>
                            </ul>
                        )}
                    </div>

                    <div className={classes.footer__column}>
                        <label
                            onClick={handleExpandButtonClick("media")}
                            className={classes.footer__label}
                        >
                            {MEDIA.LABEL}
                            <ExpandIcon
                                className={classNames(
                                    classes.footer__expand,
                                    expandedState.media && classes["footer__expand--rotated"]
                                )}
                            />
                        </label>
                        {expandedState.media && (
                            <ul className={classes.footer__list}>
                                <li>
                                    <Link
                                        href={MEDIA.ITEMS[0].LINK}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {MEDIA.ITEMS[0].TEXT}
                                    </Link>
                                </li>
                                <li>
                                    <div className={classes.footer__socials}>
                                        {SOCIALS.map((social, index) => (
                                            <a
                                                className={classes["footer__socials--link"]}
                                                key={index}
                                                href={social.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={social.ariaLabel}
                                            >
                                                <social.icon className={classes["footer__icon"]} />
                                            </a>
                                        ))}
                                    </div>
                                </li>
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            <DownloadApp />
            <div className={classes.footer__wrapper}>
                <div className={classes.footer__divider} />
                <div className={classes.footer__bottom}>
                    <span className={classes.footer__copyright}>
                        {CURRENT_YEAR} {COPYRIGHT_NOTICE}
                    </span>
                    <Payments />
                    <div className={classes.footer__documents}>
                        <ul className={classes.footer__list}>
                            {DOCUMENTS.ITEMS.map((item, index) => (
                                <li key={index}>
                                    <Link
                                        href={item.LINK}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={item.ARIA_LABEL}
                                    >
                                        {item.TEXT}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;